import { Inject, Injectable, LOCALE_ID } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { switchMap, catchError, tap, withLatestFrom, map, mergeMap, of, exhaustMap, filter } from 'rxjs'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { SelectedItemApiService } from './selected-item-api.service'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { SelectedItemFacade } from './selected-item.facade'
import { HttpErrorResponse } from '@angular/common/http'
import { GbDialogData } from 'libs/gb-front/shared/util/src/lib/gb-message-dialog/message-data.models'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'
import { GbCurrencyPipe } from 'libs/gb-front/shared/pipes/src/lib/gb-currency-pipe/gb-currency.pipe'
import { GbConfirmDialogComponent } from 'libs/gb-front/shared/feature/src/lib/gb-confirm-dialog/gb-confirm-dialog.component'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import { TranslateService } from '@ngx-translate/core'
import { GbAddLiveBidPaymentDialogComponent } from 'libs/gb-front/payment/util/src/lib/gb-add-live-bid-payment-dialog/gb-add-live-bid-payment-dialog.component'
import { AddLiveBidPaymentResultEnum } from 'libs/gb-front/payment/util/src/lib/gb-payment.models'
import { Action } from '@ngrx/store'
import { GbMessageDialogService } from 'libs/gb-front/shared/util/src/lib/gb-message-dialog/gb-message-dialog.service'
import { GbDisplayNamePipe } from 'libs/shared/utils/pipes/src/lib/gb-display-name-pipe/gb-display-name.pipe'
import * as SelectedItemActions from './selected-item.actions'
import { LiveBidPayload } from './selected-item.models'
import { GbPaymentService } from 'libs/gb-front/payment/util/src/lib/gb-payment.service'

@Injectable()
export class SelectedItemBidEffects {
  openConfirmBidDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.openConfirmBidDialog),
      withLatestFrom(
        this.selectedItemFacade.selectedItem$,
        this.selectedItemFacade.currentAmount$,
        this.auctionFacade.auctionCurrency$,
      ),
      exhaustMap(([action, item, amount, currencyCode]) => {
        return this.gbFrontDialogService.getDialogObs(GbConfirmDialogComponent, {
          mobileFullScreen: false,
          disableClose: true,
          maxWidth: '90vw',
          data: {
            header: 'ITEM.BID.CONFIRM.HEADER',
            description: this.translateService.instant('ITEM.BID.CONFIRM.MESSAGE', {
              amount: GbCurrencyPipe.getCurrencySymbol(amount, currencyCode, this.locale),
              itemName: item?.name,
            }),
            image: item && item.images?.length ? item.images[0].image.medium_image : '',
            confirmButtonText: 'ITEM.BID.CONFIRM.CONFIRM_BUTTON',
            cancelButtonText: 'ITEM.BID.CONFIRM.CANCEL_BUTTON',
          },
        })
      }),
      filter((result) => !!result),
      map(() => SelectedItemActions.placeBid()),
    ),
  )

  placeBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.placeBid),
      withLatestFrom(
        this.authFacade.authentication$,
        this.selectedProfileFacade.selectedProfileId$,
        this.selectedItemFacade.selectedItem$,
        this.auctionFacade.auctionUrlName$,
        this.selectedItemFacade.currentAmount$,
      ),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.PlaceBid, 'ITEM.BID.PLACING_BID')),
      mergeMap(([, authentication, selectedProfileId, item, urlName, amount]) =>
        this.selectedItemApiService
          .createBid(urlName, {
            amount: amount,
            profile: selectedProfileId,
            bidding_item: item?.bidding_item?.uuid,
            delegate: authentication.profile.profile,
          })
          .pipe(
            map(() => {
              const payload = {
                requiresPayment: false,
                payment: undefined,
              }
              return SelectedItemActions.placeBidSuccess(payload)
            }),
            catchError((error: HttpErrorResponse) => of(SelectedItemActions.placeBidFailure({ error }))),
          ),
      ),
    ),
  )

  placeLiveBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.placeLiveBid),
      withLatestFrom(
        this.authFacade.authProfile$,
        this.selectedItemFacade.selectedItem$,
        this.auctionFacade.auctionUrlName$,
        this.selectedProfileFacade.selectedProfile$,
        this.selectedItemFacade.currentAmount$,
        this.auctionFacade.selectedPaymentMethod$,
        this.gbPaymentService.tippingFeeAmount$,
        this.gbPaymentService.applyPaymentMethodFee$,
        this.gbPaymentService.donationAmount$,
      ),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.PlaceBid, 'ITEM.BID.PLACING_BID')),
      mergeMap(
        ([
          action,
          authProfile,
          item,
          urlName,
          selectedProfile,
          amount,
          selectedPaymentMethod,
          tippingFeeAmount,
          applyPaymentMethodFee,
          donationAmount,
        ]) => {
          const liveBidPayload: LiveBidPayload = {
            amount: amount,
            profile: selectedProfile?.uuid,
            bidding_item: item?.bidding_item?.uuid,
            delegate: authProfile?.profile,
            donation_amount: donationAmount,
          }

          if (tippingFeeAmount) {
            liveBidPayload.tipping_fee = tippingFeeAmount
          }

          if (action.payNow) {
            liveBidPayload.payment_method = selectedPaymentMethod?.uuid
            liveBidPayload.apply_fee = applyPaymentMethodFee
          }

          return this.selectedItemApiService.createLiveBid(urlName, liveBidPayload).pipe(
            map((response) => {
              let payload = {}

              if (response.payment_pending) {
                payload = {
                  requiresPayment: !!response.payment_pending,
                  payment: response.payment,
                }
              }

              return SelectedItemActions.placeLiveBidSuccess(payload)
            }),
            catchError(async (error: HttpErrorResponse) => SelectedItemActions.placeBidFailure({ error })),
          )
        },
      ),
    ),
  )

  openAddLiveBidPaymentDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SelectedItemActions.openAddLiveBidPaymentDialog),
      exhaustMap((_) => this.gbFrontDialogService.getDialogObs(GbAddLiveBidPaymentDialogComponent)),
      switchMap((result) => {
        const actions: Action[] = []

        if (result === AddLiveBidPaymentResultEnum.PayNow) {
          // proceed to payment
          actions.push(SelectedItemActions.openPaymentDialog())
        } else if (result === AddLiveBidPaymentResultEnum.PayLater) {
          // place live bid without payment
          actions.push(SelectedItemActions.placeLiveBid({ payNow: false }))
        }
        return actions
      }),
    )
  })

  placeBidFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.placeBidFailure),
        tap((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error.error)
          const messageBody = errorMessage ? `${errorMessage}` : 'ITEM_ERROR_MESSAGE_GENERIC'

          return this.gbMessageDialogService.showDialog({
            title: 'ITEM_ERROR_MESSAGE_HEADER',
            body: messageBody,
            messageType: 'warning',
          })
        }),
      ),
    { dispatch: false },
  )

  showBidSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.finaliseBidSuccess),
        withLatestFrom(
          this.authFacade.isVolunteer$,
          this.selectedProfileFacade.selectedProfile$,
          this.selectedItemFacade.selectedItem$,
        ),
        map(([action, isVolunteer, selectedProfile, item]) => {
          const successMessage = item?.bidding_item?.sealed_bid_item
            ? 'ITEM.BID.SUCCESS_MESSAGE_DESCRIPTION_SEALED_BID'
            : 'ITEM.BID.SUCCESS_MESSAGE_DESCRIPTION'

          let dialogPayload: GbDialogData = {
            title: this.translateService.instant('ITEM.BID.SUCCESS_MESSAGE_HEADER', {
              user: selectedProfile?.first_name,
              id: item?.alias,
            }),
            body: this.translateService.instant(successMessage, {
              item: item?.name,
            }),
            activityFeed: true,
            messageType: 'success',
            item: item,
          }

          if (isVolunteer) {
            const successMessage = item?.bidding_item?.sealed_bid_item
              ? 'ITEM.BID.SUCCESS_MESSAGE_DESCRIPTION_SEALED_BID_VOLUNTEER'
              : 'ITEM.BID.SUCCESS_MESSAGE_DESCRIPTION_VOLUNTEER'

            dialogPayload = {
              title: this.translateService.instant('ITEM.BID.SUCCESS_MESSAGE_HEADER_VOLUNTEER', {
                user: this.gbDisplayNamePipe.transform(selectedProfile),
                id: item?.alias,
              }),
              body: this.translateService.instant(successMessage, {
                user: this.gbDisplayNamePipe.transform(selectedProfile),
                item: item?.name,
              }),
              messageType: 'success',
              item: item,
            }
          }

          this.gbMessageDialogService.showDialog(dialogPayload)
        }),
      ),
    { dispatch: false },
  )

  hideBidLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SelectedItemActions.placeBidSuccess,
          SelectedItemActions.placeBidFailure,
          SelectedItemActions.placeLiveBidSuccess,
        ),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.PlaceBid)),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private selectedItemApiService: SelectedItemApiService,
    private selectedProfileFacade: SelectedProfileFacade,
    private selectedItemFacade: SelectedItemFacade,
    private authFacade: AuthFacade,
    private auctionFacade: AuctionFacade,
    private loaderService: GbLoaderService,
    private gbFrontDialogService: GbFrontDialogService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbMessageDialogService: GbMessageDialogService,
    private gbDisplayNamePipe: GbDisplayNamePipe,
    private translateService: TranslateService,
    private gbPaymentService: GbPaymentService,
    @Inject(LOCALE_ID) public locale: string,
  ) {}
}
